import { useState } from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { ETransportCustomerSubscriptionDto } from '@/generated/model';
import { fullDateNoSecond } from '@/utilities/dateUtils';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';
import type { SubscriptionCustomerOption } from './AddSubscriptionForm';
import { getSubscriptionUnavailableReasonKey } from './subscriptionUtils';

export type SubscriptionActionResult = { status: 'success' } | { status: 'protected' | 'error'; message: string };

export function SubscriptionCard({
  subscription,
  customer,
  customerLabel,
  isMutating,
  onReactivate,
  onRevoke,
}: {
  subscription: ETransportCustomerSubscriptionDto;
  customer: SubscriptionCustomerOption;
  customerLabel?: string;
  isMutating: boolean;
  onReactivate: (vat: string) => Promise<SubscriptionActionResult>;
  onRevoke: (vat: string) => Promise<SubscriptionActionResult>;
}) {
  const { t } = useTranslation();
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [reactivateDialogOpen, setReactivateDialogOpen] = useState(false);
  const active = subscription.revokedAtMicros == null;
  const canPerformAction = active ? subscription.canRevoke === true : subscription.canReactivate === true;
  const unavailableReason = active ? subscription.revokeUnavailableReason : subscription.reactivateUnavailableReason;
  const subscriptionVat = subscription.vat ?? '';

  function handleSubscriptionAction() {
    if (active) {
      setRevokeDialogOpen(true);
    } else {
      setReactivateDialogOpen(true);
    }
  }

  return (
    <>
      <Card variant="outlined" data-qa-element-id={`etr_subscription_${subscriptionVat}`}>
        <CardContent>
          <Stack sx={{ gap: 1 }}>
            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Typography variant="subtitle1" sx={{ overflowWrap: 'anywhere' }}>
                {subscriptionVat}
              </Typography>
              <Chip
                size="small"
                color={active ? 'success' : 'default'}
                icon={active ? <CheckCircleOutlineIcon /> : undefined}
                label={t(`etransport.subscriptions.status.${active ? 'active' : 'revoked'}`)}
              />
            </Stack>
            {customerLabel && (
              <Typography variant="body2" color="text.secondary">
                {customerLabel}
              </Typography>
            )}
            {subscription.subscribedAtMicros !== undefined && (
              <Typography variant="body2" color="text.secondary">
                {t('etransport.subscriptions.subscribedat', {
                  date: fullDateNoSecond(subscription.subscribedAtMicros, t),
                  interpolation: { escapeValue: false },
                })}
              </Typography>
            )}
            {subscription.revokedAtMicros != null && (
              <Typography variant="body2" color="text.secondary">
                {t('etransport.subscriptions.revokedat', {
                  date: fullDateNoSecond(subscription.revokedAtMicros, t),
                  interpolation: { escapeValue: false },
                })}
              </Typography>
            )}
            {canPerformAction ? (
              <Stack direction="row" sx={{ justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1 }}>
                <Button
                  size="small"
                  color={active ? 'error' : 'primary'}
                  onClick={handleSubscriptionAction}
                  disabled={isMutating || subscriptionVat === ''}
                  data-qa-element-id={active ? 'etr_subscription_revoke' : 'etr_subscription_reactivate'}
                >
                  {t(`etransport.subscriptions.${active ? 'revoke' : 'reactivate'}`)}
                </Button>
              </Stack>
            ) : (
              <Alert severity="info">{t(getSubscriptionUnavailableReasonKey(unavailableReason))}</Alert>
            )}
          </Stack>
        </CardContent>
      </Card>

      <RevokeSubscriptionDialog
        open={revokeDialogOpen}
        subscription={subscription}
        customer={customer}
        isMutating={isMutating}
        onClose={() => setRevokeDialogOpen(false)}
        onConfirm={() => onRevoke(subscriptionVat)}
      />
      <ReactivateSubscriptionDialog
        open={reactivateDialogOpen}
        subscription={subscription}
        customer={customer}
        isMutating={isMutating}
        onClose={() => setReactivateDialogOpen(false)}
        onConfirm={() => onReactivate(subscriptionVat)}
      />
    </>
  );
}

function RevokeSubscriptionDialog({
  open,
  subscription,
  customer,
  isMutating,
  onClose,
  onConfirm,
}: {
  open: boolean;
  subscription: ETransportCustomerSubscriptionDto;
  customer: SubscriptionCustomerOption;
  isMutating: boolean;
  onClose: () => void;
  onConfirm: () => Promise<SubscriptionActionResult>;
}) {
  const { t } = useTranslation();
  const [actionMessage, setActionMessage] = useState<string>();

  function handleClose() {
    setActionMessage(undefined);
    onClose();
  }

  async function handleConfirm() {
    setActionMessage(undefined);
    const result = await onConfirm();
    if (result.status === 'success') {
      handleClose();
    } else {
      setActionMessage(result.message);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={isMutating ? undefined : handleClose}
      aria-labelledby="etr-subscription-revoke-title"
      aria-describedby="etr-subscription-revoke-description"
      slotProps={{ paper: { sx: dialogPaperSx() } }}
    >
      <DialogTitle id="etr-subscription-revoke-title" component="h6" color="error">
        {t('etransport.subscriptions.revokedialog.title')}
      </DialogTitle>
      <DialogContent>
        <SubscriptionSummary customer={customer} subscription={subscription} active />
        <Typography id="etr-subscription-revoke-description" variant="body1" sx={{ mt: 2, mb: 1 }}>
          {t('etransport.subscriptions.revokedialog.message')}
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 3 }}>
          <Typography component="li" variant="body2">
            {t('etransport.subscriptions.revokedialog.newfreights')}
          </Typography>
          <Typography component="li" variant="body2">
            {t('etransport.subscriptions.revokedialog.synchronized')}
          </Typography>
          <Typography component="li" variant="body2">
            {t('etransport.subscriptions.revokedialog.queued')}
          </Typography>
          <Typography component="li" variant="body2">
            {t('etransport.subscriptions.revokedialog.others')}
          </Typography>
        </Box>
        {actionMessage && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {actionMessage}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={isMutating}
          data-qa-element-id="etr_subscription_revoke_cancel"
        >
          {t('etransport.subscriptions.revokedialog.cancel')}
        </Button>
        <Button
          onClick={() => void handleConfirm()}
          variant="contained"
          color="error"
          disabled={isMutating}
          data-qa-element-id="etr_subscription_revoke_confirm"
        >
          {isMutating && <CircularProgress size={20} color="inherit" />}
          {t('etransport.subscriptions.revokedialog.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ReactivateSubscriptionDialog({
  open,
  subscription,
  customer,
  isMutating,
  onClose,
  onConfirm,
}: {
  open: boolean;
  subscription: ETransportCustomerSubscriptionDto;
  customer: SubscriptionCustomerOption;
  isMutating: boolean;
  onClose: () => void;
  onConfirm: () => Promise<SubscriptionActionResult>;
}) {
  const { t } = useTranslation();
  const [actionMessage, setActionMessage] = useState<string>();

  function handleClose() {
    setActionMessage(undefined);
    onClose();
  }

  async function handleConfirm() {
    setActionMessage(undefined);
    const result = await onConfirm();
    if (result.status === 'success') {
      handleClose();
    } else {
      setActionMessage(result.message);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={isMutating ? undefined : handleClose}
      aria-labelledby="etr-subscription-reactivate-title"
      aria-describedby="etr-subscription-reactivate-description"
      slotProps={{ paper: { sx: dialogPaperSx() } }}
    >
      <DialogTitle id="etr-subscription-reactivate-title" component="h6">
        {t('etransport.subscriptions.reactivatedialog.title')}
      </DialogTitle>
      <DialogContent>
        <SubscriptionSummary customer={customer} subscription={subscription} active={false} />
        <Typography id="etr-subscription-reactivate-description" variant="body1" sx={{ mt: 2 }}>
          {t('etransport.subscriptions.reactivatedialog.message')}
        </Typography>
        {actionMessage && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {actionMessage}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={isMutating}
          data-qa-element-id="etr_subscription_reactivate_cancel"
        >
          {t('etransport.subscriptions.reactivatedialog.cancel')}
        </Button>
        <Button
          onClick={() => void handleConfirm()}
          variant="contained"
          disabled={isMutating}
          data-qa-element-id="etr_subscription_reactivate_confirm"
        >
          {isMutating && <CircularProgress size={20} color="inherit" />}
          {t('etransport.subscriptions.reactivatedialog.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function SubscriptionSummary({
  customer,
  subscription,
  active,
}: {
  customer: SubscriptionCustomerOption;
  subscription: ETransportCustomerSubscriptionDto;
  active: boolean;
}) {
  const { t } = useTranslation();
  const timestamp = active ? subscription.subscribedAtMicros : subscription.revokedAtMicros;

  return (
    <Box sx={{ p: 2, backgroundColor: 'var(--mui-palette-contrast-background)', borderRadius: 1 }}>
      <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
        <strong>{t('etransport.subscriptions.customer')}:</strong> {customer.name} ({customer.id})
      </Typography>
      <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
        <strong>{t('etransport.subscriptions.vat')}:</strong> {subscription.vat ?? ''}
      </Typography>
      <Typography variant="body2">
        <strong>{t('etransport.subscriptions.status.title')}:</strong>{' '}
        {t(`etransport.subscriptions.status.${active ? 'active' : 'revoked'}`)}
      </Typography>
      {timestamp != null && (
        <Typography variant="body2">
          {t(`etransport.subscriptions.${active ? 'subscribedat' : 'revokedat'}`, {
            date: fullDateNoSecond(timestamp, t),
            interpolation: { escapeValue: false },
          })}
        </Typography>
      )}
    </Box>
  );
}

function dialogPaperSx() {
  return isPhoneOrHighScreen()
    ? {
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        m: 0,
        width: '100%',
        maxWidth: '100%',
        borderRadius: '16px 16px 0 0',
        pb: 'var(--bottom-nav-bar-height)',
      }
    : {};
}
